/**
 * Union Overland - Two-Step Upsell Modal
 * File: assets/upsell-modal.js
 *
 * Flow:
 *   ATC on Rocky Mountain Tent product
 *   -> Add tent to cart silently
 *   -> Show Step 1 (Power Stations: 5 options, first = free/default)
 *       Next     -> add the SELECTED station (free or paid upgrade) to cart
 *       Decline  -> add the FREE station to cart, keep moving
 *   -> Always show Step 2 (Solar Panels: multiple options)
 *       Accept  -> add selected solar panel variant to cart
 *       Decline -> open cart drawer
 *   -> Open the cart drawer (falls back to /cart if no drawer found)
 *
 * Dismissing the popup (X, outside click, Esc) while still on Step 1
 * adds the discounted power station first, since it is always included.
 *
 * Note on discounts: the free station is added as a normal line item.
 * Set up a Shopify automatic discount to bring its price to 0.
 */

(function () {
  'use strict';

  /* --- Guard: only run if config is present --- */
  if (!window.UO_UPSELL_CONFIG) return;

  const CFG = window.UO_UPSELL_CONFIG;

  /* --- State --- */
  let selectedStep1VariantId = null;
  let selectedStep2VariantId = null;
  let step1Committed = false; // true once the Step 1 choice has been added

  /* --- DOM Refs --- */
  const overlay       = document.getElementById('uo-upsell-overlay');
  const step1El       = document.getElementById('uo-step-1');
  const step2El       = document.getElementById('uo-step-2');
  const loadingEl     = document.getElementById('uo-loading');
  const closeBtn      = document.getElementById('uo-close-btn');
  const step1Next     = document.getElementById('uo-step1-next');
  const step1Decline  = document.getElementById('uo-step1-decline');
  const step2Add      = document.getElementById('uo-step2-add');
  const step2Decline  = document.getElementById('uo-step2-decline');
  const step1Grid     = document.getElementById('uo-step1-products');
  const step2Grid     = document.getElementById('uo-step2-products');
  const progressSteps = document.querySelectorAll('.uo-progress-step');

  /* ─────────────────────────────────────────
     PRODUCT CARD INIT
     Cards are already rendered by Liquid.
     We just wire up click events and auto-select first.
  ───────────────────────────────────────── */
  function initProductCards(container, selectedSetter) {
    if (!container) return;
    const cards = container.querySelectorAll('.uo-product-card');
    if (!cards.length) return;

    /* Reset selection to the default (first) card every time the modal
       opens. The page no longer reloads on the way to the cart, so the
       DOM persists between opens and a stale selection would otherwise
       remain, showing two selected cards. */
    cards.forEach(c => c.classList.remove('selected'));
    const firstCard = cards[0];
    firstCard.classList.add('selected');
    selectedSetter(firstCard.dataset.variantId, firstCard.dataset.isFree === 'true');

    /* Bind click handlers once per grid to avoid stacking listeners */
    if (container.dataset.uoBound === 'true') return;
    container.dataset.uoBound = 'true';

    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        selectedSetter(card.dataset.variantId, card.dataset.isFree === 'true');
      });
    });
  }

  /* Returns the variant id of the free (first) power station, if present */
  function getFreeStationVariantId() {
    if (!step1Grid) return null;
    const freeCard = step1Grid.querySelector('.uo-product-card[data-is-free="true"]')
                  || step1Grid.querySelector('.uo-product-card');
    return freeCard ? freeCard.dataset.variantId : null;
  }

  /* Adds the free station if the Step 1 choice has not been committed yet.
     Used by the dismiss paths (X / outside click / Esc) on Step 1. */
  async function ensureFreeStationAdded() {
    if (step1Committed) return;
    step1Committed = true;

    const freeId = getFreeStationVariantId();
    if (!freeId) return;

    showLoading(true);
    try {
      await addToCart(freeId);
    } catch (err) {
      console.error('[UO Upsell] Free station add (on close) failed:', err);
    }
    showLoading(false);
  }

  /* ─────────────────────────────────────────
     MODAL OPEN / CLOSE
  ───────────────────────────────────────── */
  function openModal() {
    step1Committed = false;
    overlay.classList.add('active');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setStep(1);
  }

  function closeModal() {
    overlay.classList.remove('active');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ─────────────────────────────────────────
     STEP SWITCHING
  ───────────────────────────────────────── */
  function setStep(num) {
    step1El.classList.toggle('hidden', num !== 1);
    step2El.classList.toggle('hidden', num !== 2);

    progressSteps.forEach(s => {
      const sNum = parseInt(s.dataset.step);
      s.classList.toggle('active', sNum === num);
      s.classList.toggle('done', sNum < num);
    });

    const activeStep = num === 1 ? step1El : step2El;
    activeStep.classList.add('step-enter');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => activeStep.classList.remove('step-enter'));
    });
  }

  /* ─────────────────────────────────────────
     CART API
  ───────────────────────────────────────── */
  async function addToCart(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity }] })
    });
    if (!res.ok) throw new Error('Cart add failed');
    return res.json();
  }

  function showLoading(show) {
    loadingEl.classList.toggle('hidden', !show);
    step1El.style.opacity = show ? '0.4' : '';
    step2El.style.opacity = show ? '0.4' : '';
  }

  /* ─────────────────────────────────────────
     CART DRAWER (with fallback to /cart)
     We do NOT edit the cart drawer section. We only refresh its
     content container and count at runtime, then open it.
  ───────────────────────────────────────── */
  const CART_DRAWER_SELECTORS = [
    'cart-drawer',
    '#Cart-Drawer',
    '.cart-drawer',
    '[data-cart-drawer]'
  ];

  function findCartDrawer() {
    for (const sel of CART_DRAWER_SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  /* Force freshly injected images to load now. The theme's lazy-load
     observer was set up on page load and never sees nodes we inject,
     so without this the images sit on their placeholder until the
     theme re-renders the line items (e.g. on a quantity change). */
  function activateLazyImages(scope) {
    if (!scope) return;
    scope.querySelectorAll('img').forEach(img => {
      const realSrc    = img.dataset.src || img.getAttribute('src');
      const realSrcset = img.dataset.srcset || img.getAttribute('srcset');
      img.setAttribute('loading', 'eager');
      if (realSrcset) img.setAttribute('srcset', realSrcset);
      if (realSrc) img.setAttribute('src', realSrc); // reassign triggers the load

      /* The theme clears its blur/shimmer by adding 'image-lazy-loaded'.
         Its observer never sees nodes we inject, so we add it ourselves
         once the bitmap is ready. */
      const markLoaded = () => img.classList.add('image-lazy-loaded');
      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
      } else {
        img.addEventListener('load', markLoaded, { once: true });
      }
    });
  }

  /* Re-render the drawer content from the server using the Section
     Rendering API. The drawer exposes its section id via data-section. */
  async function refreshCartDrawer(drawerEl) {
    const sectionId = drawerEl.dataset.section;
    if (!sectionId) return;

    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
      const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) return;

      const text = await res.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');

      /* Swap only the drawer content container */
      const freshDetails   = parsed.querySelector('#Cart-Drawer-Details');
      const currentDetails = drawerEl.querySelector('#Cart-Drawer-Details');
      if (freshDetails && currentDetails) {
        currentDetails.innerHTML = freshDetails.innerHTML;
        activateLazyImages(currentDetails);
      }

      /* Update the cart count everywhere it appears (drawer + header) */
      const freshCount = parsed.querySelector('.cart-count');
      if (freshCount) {
        document.querySelectorAll('.cart-count').forEach(el => {
          el.textContent = freshCount.textContent;
        });
      }

      /* Cart now has items, so drop any empty-state styling on the host */
      drawerEl.classList.remove('is-empty', 'cart-drawer--empty');
    } catch (err) {
      console.warn('[UO Upsell] Cart drawer refresh failed:', err);
    }
  }

  function openCartDrawerEl(drawerEl) {
    /* This theme uses show() / hide() on its modal elements */
    if (typeof drawerEl.show === 'function') { drawerEl.show(); return; }
    if (typeof drawerEl.open === 'function') { drawerEl.open(); return; }

    /* Fallback: toggle the common open classes */
    ['active', 'is-open', 'open', 'drawer--active'].forEach(c => drawerEl.classList.add(c));
    drawerEl.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
  }

  async function openCart() {
    closeModal();

    const drawer = findCartDrawer();
    if (!drawer) {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      return;
    }

    await refreshCartDrawer(drawer);
    openCartDrawerEl(drawer);
  }

  /* Dismiss the popup: make sure the free station is in the cart
     (if Step 1 was not committed), then open the drawer. */
  async function dismissFlow() {
    await ensureFreeStationAdded();
    openCart();
  }

  /* ─────────────────────────────────────────
     ATC INTERCEPT
  ───────────────────────────────────────── */
  function isRockyMountainProduct(form) {
    if (form.querySelector('[data-upsell-trigger="true"]')) return true;
    return false;
  }

  document.addEventListener('click', async function (e) {
    const btn = e.target.closest('button[name="add"]');
    if (!btn) return;

    const form = btn.closest('form');
    if (!form) return;
    if (!isRockyMountainProduct(form)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    /* Hand-off with tent-addon-selector.js: it will still append the
       selected add-ons to our add request, but this upsell flow owns
       the cart drawer and opens it only at the end. The flag tells the
       add-on selector not to pop the drawer mid-flow. */
    window.__UO_UPSELL_HANDLING_ADD = true;

    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return;

    showLoading(true);

    try {
      await addToCart(variantInput.value);
    } catch (err) {
      console.error('[UO Upsell] Failed to add tent to cart:', err);
      showLoading(false);
      form.submit();
      return;
    }

    showLoading(false);

    /* Wire up card clicks - cards already rendered by Liquid */
    initProductCards(
      step1Grid,
      (id, isFree) => { selectedStep1VariantId = { id, isFree }; }
    );
    initProductCards(
      step2Grid,
      (id) => { selectedStep2VariantId = id; }
    );

    openModal();
  }, true);

  /* ─────────────────────────────────────────
     BUTTON HANDLERS
  ───────────────────────────────────────── */

  /* Step 1: Next - always add the selected station (free or upgrade) */
  step1Next && step1Next.addEventListener('click', async () => {
    const variantId = selectedStep1VariantId
      ? selectedStep1VariantId.id
      : getFreeStationVariantId();

    if (variantId) {
      showLoading(true);
      try {
        await addToCart(variantId);
      } catch (err) {
        console.error('[UO Upsell] Step 1 add to cart failed:', err);
      }
      showLoading(false);
    }

    step1Committed = true;
    setStep(2);
  });

  /* Step 1: Decline - keep the free station, add it, then move to step 2 */
  step1Decline && step1Decline.addEventListener('click', async () => {
    const freeId = getFreeStationVariantId();

    if (freeId) {
      showLoading(true);
      try {
        await addToCart(freeId);
      } catch (err) {
        console.error('[UO Upsell] Free station add to cart failed:', err);
      }
      showLoading(false);
    }

    step1Committed = true;
    setStep(2);
  });

  /* Step 2: Add solar panel */
  step2Add && step2Add.addEventListener('click', async () => {
    if (!selectedStep2VariantId) { openCart(); return; }

    showLoading(true);
    try {
      await addToCart(selectedStep2VariantId);
    } catch (err) {
      console.error('[UO Upsell] Step 2 add to cart failed:', err);
    }
    showLoading(false);
    openCart();
  });

  /* Step 2: Decline */
  step2Decline && step2Decline.addEventListener('click', () => {
    openCart();
  });

  /* Close button - on Step 1 this still adds the free station */
  closeBtn && closeBtn.addEventListener('click', () => {
    dismissFlow();
  });

  /* Click outside modal */
  overlay && overlay.addEventListener('click', (e) => {
    if (e.target === overlay) dismissFlow();
  });

  /* ESC key */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('active')) dismissFlow();
  });

})();